import { useInfiniteQuery, UseInfiniteQueryOptions } from 'react-query';
import { fetcher, ServerErrorResponse, performanceReportingApiUrl, createParamString } from 'data_sources/constants';

export interface TemplateVersion {
  id: number;
  versionNumber: number;
  status: string;
  effectiveDate: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  publishedBy: string;
  inactivatedDate: string | null;
}

export interface TemplateVersionsResponse {
  content: TemplateVersion[];
  count: number;
  nextToken: string | null;
}

// Fetch template versions from API
export const getTemplateVersions = async (
  templateId: number,
  params: Record<string, any> = {},
  signal?: AbortSignal,
  onError?: (errors: ServerErrorResponse) => void
): Promise<TemplateVersionsResponse> => {
  try {
    const url = `${performanceReportingApiUrl()}/performance/templates/v1/templates/${templateId}/versions`;

    const queryParams = createParamString(params);
    const fullUrl = queryParams ? `${url}?${queryParams}` : url;

    const response = await fetcher({
      method: 'GET',
      url: fullUrl,
      options: { signal },
    });

    if (response.errorDetails) {
      if (onError) onError(response);
      throw new Error(JSON.stringify(response));
    }

    return response;
  } catch (error: unknown) {
    if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
      onError(error as ServerErrorResponse);
    }
    throw error;
  }
};

export const useTemplateVersions = (
  templateId: number,
  params: Record<string, any> = {},
  options: Partial<UseInfiniteQueryOptions<TemplateVersionsResponse, Error>> = {},
  onError?: (errors: ServerErrorResponse) => void
) => {
  return useInfiniteQuery({
    queryKey: ['templateVersions', templateId, params],
    queryFn: async ({ pageParam, signal }) => {
      return getTemplateVersions(
        templateId,
        {
          ...params,
          nextToken: pageParam,
        },
        signal,
        onError
      );
    },
    getNextPageParam: (lastPage) => lastPage.nextToken || undefined,
    enabled: !!templateId,
    retry: false, // Simplified retry logic to match established patterns
    ...options,
  });
};
